import { useCallback, useEffect, useRef, useState } from "react";

import { safeSessionStorage, type SafeStorage } from "../utils/safeStorage";

/**
 * A drop-in replacement for ahooks' `useSessionStorageState` that is resilient to blocked web
 * storage. ahooks reads/writes the real Storage methods directly; when those throw (in-app
 * webviews, blocked site data) and `listenStorageChange` is on, the React state update — which is
 * driven by an event dispatched only after a successful write — never fires, leaving the UI
 * unresponsive. This version keeps React state as the source of truth and persists through
 * `safeStorage` (which falls back to in-memory storage), so the UI keeps working either way.
 *
 * Same-page instances sharing a key stay in sync via a custom event, matching ahooks'
 * `listenStorageChange: true` behaviour.
 */

const SYNC_EVENT = "minimatch:safe-storage-sync";

type SetState<T> = T | ((prev: T | undefined) => T);

export interface SafeStorageStateOptions<T> {
  defaultValue?: T | (() => T);
  onError?: (error: unknown) => void;
  serializer?: (value: T) => string;
  deserializer?: (value: string) => T;
  // Accepted for API compatibility with ahooks; same-page sync is always enabled.
  listenStorageChange?: boolean;
}

function createUseSafeStorageState(storage: SafeStorage) {
  return function useSafeStorageState<T>(
    key: string,
    options: SafeStorageStateOptions<T> = {},
  ): [T | undefined, (value?: SetState<T>) => void] {
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const read = (): T | undefined => {
      const opts = optionsRef.current;
      try {
        const raw = storage.getItem(key);
        if (raw != null) {
          return opts.deserializer ? opts.deserializer(raw) : (JSON.parse(raw) as T);
        }
      } catch (error) {
        opts.onError?.(error);
      }
      return typeof opts.defaultValue === "function"
        ? (opts.defaultValue as () => T)()
        : opts.defaultValue;
    };

    const [state, setState] = useState<T | undefined>(read);
    const stateRef = useRef(state);
    stateRef.current = state;

    const sync = () => {
      const fresh = read();
      stateRef.current = fresh;
      setState(fresh);
    };

    const update = useCallback(
      (value?: SetState<T>) => {
        const opts = optionsRef.current;
        const prev = stateRef.current;
        const next =
          typeof value === "function"
            ? (value as (p: T | undefined) => T)(prev)
            : (value as T | undefined);

        stateRef.current = next;
        setState(next);

        try {
          if (next === undefined) {
            storage.removeItem(key);
          } else {
            storage.setItem(key, opts.serializer ? opts.serializer(next) : JSON.stringify(next));
          }
        } catch (error) {
          opts.onError?.(error);
        }

        window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: { key } }));
      },
      [key],
    );

    // Keep other instances of the same key in sync within this page, and re-read when the key
    // changes (skipping the initial mount, which already read via the useState initialiser).
    const mounted = useRef(false);
    useEffect(() => {
      if (mounted.current) {
        sync();
      } else {
        mounted.current = true;
      }

      const handler = (event: Event) => {
        if ((event as CustomEvent<{ key: string }>).detail?.key === key) {
          sync();
        }
      };
      window.addEventListener(SYNC_EVENT, handler);
      return () => window.removeEventListener(SYNC_EVENT, handler);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return [state, update];
  };
}

export const useSafeSessionStorageState = createUseSafeStorageState(safeSessionStorage);
